import { toast } from 'react-toastify'
import { ChainProtocol, OrganizationAccountType } from '@archax/shared-types'
import { useQuery } from '@tanstack/react-query'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, CircularProgress, Link, List, ListItem, ListItemText, Typography, styled } from '@mui/material'
import { getOrganizationNativeBalances } from '../../api/organizations'
import ALGOLogo from '../../assets/currency-algo-logo.svg'
import APTLogo from '../../assets/currency-apt-logo.svg'
import ARBLogo from '../../assets/currency-arb-logo.svg'
import ETHLogo from '../../assets/currency-eth-logo.svg'
import HbarLogo from '../../assets/currency-hbar-logo.svg'
import MaticLogo from '../../assets/currency-matic-logo.svg'
import XDCLogo from '../../assets/currency-xdc-logo.png'
import SolanaLogo from '../../assets/currency-solana-logo.svg'
import XRPLogo from '../../assets/currency-xrp-logo.svg'
import XTZLogo from '../../assets/currency-xtz-logo.svg'
import XLMLogo from '../../assets/currency-xlm-logo.svg'
import SDRLogo from '../../assets/sd-icon-circle-blue.svg'
import { useGlobalContext } from '../../context'
import nativeCurrencyFormatter from '../../util/native-currency-formatter'
import onApiError from '../../util/on-api-error'
import copyToClipboard from '../../util/copy-to-clipboard'

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
  div: {
    display: 'flex',
    flexDirection: 'column',
  },
  span: {
    lineHeight: '100%',
  },
}))

const CHAIN_PROTOCOLS_WITH_ICON = [
  ChainProtocol.Algorand,
  ChainProtocol.Arbitrum,
  ChainProtocol.Aptos,
  ChainProtocol.Ethereum,
  ChainProtocol.Etherlink,
  ChainProtocol.Hedera,
  ChainProtocol.Polygon,
  ChainProtocol.Solana,
  ChainProtocol.Stellar,
  ChainProtocol.XDC,
  ChainProtocol.XRP_Ledger,
] as const

type ChainProtocolWithIcon = (typeof CHAIN_PROTOCOLS_WITH_ICON)[number]

const CHAIN_CURRENCY_ICON: Record<ChainProtocolWithIcon, string> = {
  [ChainProtocol.Algorand]: ALGOLogo,
  [ChainProtocol.Arbitrum]: ARBLogo,
  [ChainProtocol.Aptos]: APTLogo,
  [ChainProtocol.Ethereum]: ETHLogo,
  [ChainProtocol.Etherlink]: XTZLogo,
  [ChainProtocol.Hedera]: HbarLogo,
  [ChainProtocol.Polygon]: MaticLogo,
  [ChainProtocol.Solana]: SolanaLogo,
  [ChainProtocol.Stellar]: XLMLogo,
  [ChainProtocol.XDC]: XDCLogo,
  [ChainProtocol.XRP_Ledger]: XRPLogo,
}

const hasChainCurrencyIcon = (protocol: ChainProtocol): protocol is ChainProtocolWithIcon =>
  CHAIN_PROTOCOLS_WITH_ICON.includes(protocol as ChainProtocolWithIcon)

const getChainCurrencyIcon = (protocol: ChainProtocol, privacyEnabled: boolean = false): string => {
  if (privacyEnabled) {
    return SDRLogo
  }

  if (hasChainCurrencyIcon(protocol)) {
    return CHAIN_CURRENCY_ICON[protocol]
  }

  return ''
}

const PRIVACY_VARIANTS = ['public', 'privacy'] as const
type PrivacyVariant = (typeof PRIVACY_VARIANTS)[number]
type ChainListKey = `${ChainProtocol}-${PrivacyVariant}`

const getChainListKey = (protocol: ChainProtocol, privacyEnabled?: boolean): ChainListKey =>
  `${protocol}-${privacyEnabled ? 'privacy' : 'public'}` as ChainListKey

const AccountsBalances: React.FunctionComponent = () => {
  const {
    state: { user },
  } = useGlobalContext()

  const { isLoading, data } = useQuery(
    ['organization-native-balances'],
    () => getOrganizationNativeBalances(user!.organizationId),
    {
      retry: false,
      onError: onApiError,
      cacheTime: 0,
    },
  )

  if (isLoading) {
    return <CircularProgress />
  }

  return (
    <Box>
      <List>
        {data?.data.data.map((chain) => (
          <ListItem key={getChainListKey(chain.protocol as ChainProtocol, chain.privacyEnabled)}>
            <img
              alt={`${chain.protocol} icon`}
              src={getChainCurrencyIcon(chain.protocol as ChainProtocol, chain.privacyEnabled)}
              width={24}
              height={24}
            />
            <StyledListItemText sx={{ ml: 1 }}>
              {chain.address && (
                <>
                  <Typography>
                    {nativeCurrencyFormatter(chain.balance as string)} {chain.currencySymbol}
                  </Typography>
                  <div>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      <small>
                        {OrganizationAccountType[chain.type.toUpperCase() as keyof typeof OrganizationAccountType]}
                      </small>
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {chain.address.length > 12
                        ? `${chain.address.substring(0, 7)}...${chain.address.slice(-5)}`
                        : chain.address}{' '}
                      <ContentCopyIcon
                        onClick={() => {
                          copyToClipboard(chain.address as string)
                          toast.success('Address copied')
                        }}
                        sx={{ fontSize: 12, cursor: 'pointer' }}
                      />{' '}
                      <Link href={chain.explorerLink!} rel="noreferrer" target="_blank" sx={{ color: 'inherit' }}>
                        <OpenInNewIcon sx={{ fontSize: 12, cursor: 'pointer' }} />
                      </Link>
                    </Typography>
                  </div>
                </>
              )}
              {!chain.address && <Typography color="error.dark">Account unavailable</Typography>}
            </StyledListItemText>
          </ListItem>
        ))}
      </List>
    </Box>
  )
}

export default AccountsBalances
